import type { MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import CheckSquareInsideIcon from '@/assets/icons/CheckSquareInsideIcon';
import MinusSquareInsideIcon from '@/assets/icons/MinusSquareInsideIcon';
import useColor from '@/hooks/domain/useColor';
import { cn } from '@/lib/cn';
import { getAlphaColor } from '@/styles/colors/color.utils';

import { Flex } from '../base';

type Props = {
  checked: boolean;
  halfCheck?: boolean;
  size?: number;
  disabled?: boolean;
  onChange?: (status: boolean, e?: MouseEvent) => void;
  className?: string;
  style?: React.CSSProperties;
};

const Checkbox = (props: Props) => {
  const color = useColor();
  const clickHandler = (e: MouseEvent) => {
    if (!props.onChange) return;
    if (props.disabled) return;
    e.stopPropagation();
    props.onChange(!props.checked, e);
  };
  return (
    <div
      onClick={clickHandler}
      style={props.style}
      className={cn(
        'flex items-center justify-center w-6 px-1 h-5 relative cursor-pointer text-white',
        props.className,
      )}
    >
      <Background
        checked={props.checked}
        halfCheck={props.halfCheck}
        disabled={props.disabled}
      />
      <CheckWrapper>
        {props.checked && !props.halfCheck && (
          <CheckSquareInsideIcon
            color={color.FL100}
            style={{
              fontSize: props.size || 18,
            }}
          />
        )}
        {props.halfCheck && (
          <MinusSquareInsideIcon
            color={color.FL100}
            style={{
              fontSize: props.size || 18,
            }}
          />
        )}
      </CheckWrapper>
    </div>
  );
};

type StyleProps = {
  checked: boolean;
  halfCheck?: boolean;
  disabled?: boolean;
};

const Background = styled.div<StyleProps>`
  position: absolute;
  z-index: 1;
  width: 14px;
  height: 14px;
  border-radius: 2px;
  transition: all 0.2s;
  border: 1px solid transparent;
  &:hover {
    background-color: ${(props) =>
      getAlphaColor(props.theme.colors.primary100, 0.2)};
    border: 1px solid
      ${(props) => getAlphaColor(props.theme.colors.primary100, 1)};
  }
  ${(props) =>
    props.checked || props.halfCheck
      ? css`
          border: 1px solid ${(props) => props.theme.colors.primary100};
          background-color: ${(props) => props.theme.colors.primary100};
        `
      : css`
          border: 1px solid ${(props) => props.theme.colors.gray500};
          background-color: transparent;
        `}

  ${(props) =>
    props.disabled &&
    css`
      background-color: ${(props) => props.theme.colors.gray300};
      border: 1px solid ${(props) => props.theme.colors.gray300};
    `}
`;

const CheckWrapper = styled(Flex).attrs({
  a: 'center',
  j: 'center',
})`
  position: relative;
  z-index: 2;
`;

export default Checkbox;
